import type { BlockOutput, TranscriptMessage } from '../../types';
import type { SliceCreator, StreamingActions } from './types';

export const createStreamingSlice: SliceCreator<StreamingActions> = (set) => ({
  // Append a stream_chunk to the live draft. Opens a fresh draft on the
  // first chunk so callers don't have to seed one. Frames belonging to
  // a different session are dropped.
  appendStreamDelta: (sessionId, delta) =>
    set((s) => {
      const draft = s.streamingDraft;
      if (draft && draft.sessionId !== sessionId) return {};
      const insertAt = s.messagesBySession[sessionId]?.length ?? 0;
      return {
        streamingDraft: draft
          ? {
              ...draft,
              content: draft.content + delta,
              updatedAt: Date.now(),
            }
          : {
              sessionId,
              content: delta,
              subSteps: [],
              outputs: [],
              insertAt,
              updatedAt: Date.now(),
            },
      };
    }),

  // Drop accumulated draft TEXT before a (re)streamed LLM attempt so a
  // retry doesn't duplicate the already-shown prefix. Keeps sub-steps and
  // outputs (tool rows / rich blocks) — only the streaming text resets.
  resetStreamDraft: (sessionId) =>
    set((s) => {
      const draft = s.streamingDraft;
      if (!draft || draft.sessionId !== sessionId) return {};
      return { streamingDraft: { ...draft, content: '' } };
    }),

  // Add a sub-step row (tool start / action progress). If an entry with
  // the same id already exists, update it in place — tool_start followed
  // by tool_result reuses the id, so we'd otherwise show duplicate rows.
  pushSubStep: (sessionId, step) =>
    set((s) => {
      const draft = s.streamingDraft;
      if (draft && draft.sessionId !== sessionId) return {};
      const base = draft ?? {
        sessionId,
        content: '',
        subSteps: [],
        outputs: [],
        insertAt: s.messagesBySession[sessionId]?.length ?? 0,
      };
      const idx = base.subSteps.findIndex((x) => x.id === step.id);
      const subSteps =
        idx === -1
          ? [...base.subSteps, step]
          : base.subSteps.map((x, i) => (i === idx ? { ...x, ...step } : x));
      return {
        streamingDraft: { ...base, subSteps, updatedAt: Date.now() },
      };
    }),

  // Append a rich block produced by a tool_result. Opens a fresh draft
  // if none exists (rare — usually appendStreamDelta has already done so).
  //
  // Redundancy rule: a dataTable block is the canonical answer for the
  // analytical turn that produced it. Any resume blocks that arrived in
  // the same turn beforehand were intermediate data fetches the LLM did
  // to BUILD that table. Showing them alongside the table is duplicate
  // noise (the user sees three full profiles + a comparison of the same
  // three people). When a dataTable arrives, drop the preceding resume
  // blocks so the table is the answer.
  pushOutputBlock: (sessionId, block) =>
    set((s) => {
      const draft = s.streamingDraft;
      if (draft && draft.sessionId !== sessionId) return {};
      const base = draft ?? {
        sessionId,
        content: '',
        subSteps: [],
        outputs: [],
        insertAt: s.messagesBySession[sessionId]?.length ?? 0,
      };
      const filtered =
        block.type === 'dataTable'
          ? base.outputs.filter((b) => b.type !== 'resume')
          : base.outputs;
      return {
        streamingDraft: {
          ...base,
          outputs: [...filtered, block],
          updatedAt: Date.now(),
        },
      };
    }),

  // Patch an existing sub-step (used by tool_result to flip status).
  // No-op if the draft is gone or the id isn't there.
  patchSubStep: (sessionId, id, patch) =>
    set((s) => {
      const draft = s.streamingDraft;
      if (!draft || draft.sessionId !== sessionId) return {};
      return {
        streamingDraft: {
          ...draft,
          subSteps: draft.subSteps.map((x) =>
            x.id === id ? { ...x, ...patch } : x
          ),
        },
      };
    }),

  // Swap the suggestedActions on every candidate/actionResult block
  // whose `toolCallId` matches — both in the live streaming draft and in
  // the session's committed transcript. Pure update; identity-stable
  // when nothing matched so React doesn't re-render unnecessarily.
  patchBlockChips: (sessionId, toolCallId, chips) =>
    set((s) => {
      const patchBlock = (b: BlockOutput): BlockOutput => {
        if (b.type !== 'candidate' && b.type !== 'actionResult') return b;
        if (b.data.toolCallId !== toolCallId) return b;
        return { ...b, data: { ...b.data, suggestedActions: chips } };
      };
      // Streaming draft (current turn).
      const draft = s.streamingDraft;
      const nextDraft =
        draft && draft.sessionId === sessionId
          ? { ...draft, outputs: draft.outputs.map(patchBlock) }
          : draft;
      // Committed transcript (prior turns).
      const existing = s.messagesBySession[sessionId];
      if (!existing) return { streamingDraft: nextDraft };
      const nextMessages = existing.map((m) =>
        m.role === 'assistant'
          ? { ...m, outputs: m.outputs.map(patchBlock) }
          : m
      );
      return {
        streamingDraft: nextDraft,
        messagesBySession: {
          ...s.messagesBySession,
          [sessionId]: nextMessages,
        },
      };
    }),

  clearStreamingDraft: () => set({ streamingDraft: null }),

  setTurnScope: (sessionId, patch) =>
    set((s) => ({
      turnScopeBySession: {
        ...s.turnScopeBySession,
        [sessionId]: {
          activeTurn: null,
          lastDoneTurn: null,
          lastAcceptedAt: 0,
          ...s.turnScopeBySession[sessionId],
          ...patch,
        },
      },
    })),

  commitStreamingDraft: () =>
    set((s) => {
      const draft = s.streamingDraft;
      if (!draft) return { streamingDraft: null };
      // Nothing worth committing if there's no text AND no blocks.
      if (!draft.content && draft.outputs.length === 0) {
        return { streamingDraft: null };
      }
      const existing = s.messagesBySession[draft.sessionId] ?? [];
      // Assemble the message: text first (the LLM's prose summary),
      // then the rich blocks collected from tool_result events in order.
      const outputs: BlockOutput[] = [];
      if (draft.content) {
        outputs.push({ type: 'text', data: { content: draft.content } });
      }
      outputs.push(...draft.outputs);
      const committed: TranscriptMessage = {
        role: 'assistant',
        id: `assistant-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        outputs,
        createdAt: new Date().toISOString(),
        // Persist the action trail so the perplexity-style "sources"
        // footer survives once streaming ends. Only keep completed
        // (non-active) steps — active ones imply mid-stream and would
        // be confusing on a finished turn.
        actionTrail: draft.subSteps
          .filter((step) => step.status !== 'active')
          .map((step) => ({
            id: step.id,
            name: step.name,
            description: step.description,
            status: step.status,
            durationMs: step.durationMs,
          })),
      };
      // Splice at the anchor index captured when the draft opened. This
      // keeps the assistant reply directly after the user message that
      // triggered it even if newer user messages (e.g. a chip click) were
      // appended while streaming was still in flight.
      const idx = Math.min(Math.max(draft.insertAt, 0), existing.length);
      const next = [
        ...existing.slice(0, idx),
        committed,
        ...existing.slice(idx),
      ];
      return {
        messagesBySession: {
          ...s.messagesBySession,
          [draft.sessionId]: next,
        },
        streamingDraft: null,
      };
    }),
});
